/**
 * One quiz instance: answer state, the chat log and the result panels.
 *
 * The chat is one continuous, scrolling log. The open question's choices sit
 * in a dock stuck to its bottom edge, so they're always in the same place.
 * "Change answer" reopens only that question, with its choices under it in
 * the chat (the dock steps aside meanwhile); every other answer stays. Once
 * all questions are answered the
 * result and lead form appear below, and later changes update them in place
 * (including the lead form's hidden fields). Progress is reported to
 * analytics (see analytics.js) so drop-off can be measured.
 *
 * @package ppd
 * @since   1.0.27
 */

import { score, formatScores } from './scoring.mjs';
import pushEvent from './analytics';
import LeadForm from './lead-form';
import {
	el, format, prefersReducedMotion, getSessionId,
} from './utils';

const TYPING_MS = 1000; // Includes the dots' fade-in delay (_chat.scss).
const CHOOSE_MS = 180; // The other choices fade out (_chat.scss) before the answer moves in.

// Every movement (turn heights, scrolling, the chosen answer floating into
// place) shares one duration and curve, so they read as a single motion.
const MOTION_MS = 650;
const ease = (t) => -(Math.cos(Math.PI * t) - 1) / 2; // easeInOutSine.
const LONG_SCROLL_MS = 1200; // Long scrolls (e.g. to the result) take longer so they don't rush.
const SCROLL_MS_PER_PX = 1.4;

/**
 * Hold a turn's or the dock's height while its content changes: if the
 * chat got shorter even for a moment, the browser would clamp the scroll
 * and jump. resize() then eases it to the new height.
 *
 * @param {HTMLElement} node
 * @param {boolean}     [fromZero] Hold (and ease from) an empty node too.
 * @return {number} Height before the change.
 */
function hold(node, fromZero = false) {
	const before = node.offsetHeight;
	if ((before || fromZero) && !prefersReducedMotion()) {
		node.style.height = `${before}px`;
		node.style.overflow = 'hidden';
	}
	return before;
}

function botBubble(text, id = null) {
	return el('div', { class: 'em-quiz__row' }, el('p', { class: 'em-quiz__bubble em-quiz__bubble--bot', id, text }));
}

export default class Quiz {
	/**
	 * @param {HTMLElement} root   The [data-em-quiz] element.
	 * @param {Object}      config Output of em_quiz_public_config() plus preview/hasLead.
	 */
	constructor(root, config) {
		this.root = root;
		this.config = config;
		this.strings = config.strings;
		this.questions = config.questions;
		this.id = root.dataset.emQuizInstance;

		const find = (name) => root.querySelector(`[data-em-quiz-${name}]`);
		this.els = {
			status: find('status'),
			progress: find('progress'),
			body: find('body'),
			log: find('log'),
			result: find('result'),
			lead: find('lead'),
			form: find('form'),
			thanks: find('thanks'),
			announce: find('announce'),
			dock: find('dock'),
			dockInner: find('dock-inner'),
		};

		this.sessionId = getSessionId(config.id);
		this.dialog = root.closest('dialog');
		this.viewed = false;
		this.lead = config.hasLead && this.els.form
			? new LeadForm(this.els.form, config, { onSubmitted: () => this.onLeadSubmitted() })
			: null;

		this.timers = [];
		this.reset();

		// Running motion, advanced together each frame by tick().
		this.resizes = new Map(); // Turn => { from, to, start } heights.
		this.scrolling = null; // { from, to, start } scrollTop.
		this.flight = null; // { ghost, bubble, from, start, onLand }: an answer floating up (fly()).
		this.frame = null;
		this.tick = this.tick.bind(this);
		// The visitor scrolling takes over from an in-progress auto-scroll.
		['wheel', 'touchstart'].forEach((type) => {
			this.els.body.addEventListener(type, () => this.stopScroll(), { passive: true });
		});

		this.interacted = false;
		root.addEventListener('click', (event) => this.onClick(event));
		root.addEventListener('keydown', () => {
			this.interacted = true;
		});
		this.startWhenVisible();
	}

	reset() {
		this.timers.forEach(clearTimeout);
		this.timers = [];
		this.answers = new Array(this.questions.length).fill(null);
		this.editing = null; // Question reopened with "Change answer".
		this.revealed = 0; // Question turns added to the log.
		this.revealing = false; // A question is "typing".
		this.choosing = false; // A chosen answer is about to move into place.
		this.dockKey = null; // Question whose choices the dock shows, or null.
		this.phase = 'questions'; // questions | complete | submitted
		this.outcome = null;
		this.turns = [];
		this.started = false;
	}

	/**
	 * Modal quizzes start when first opened; inline ones when scrolled into view.
	 */
	startWhenVisible() {
		if (this.dialog) {
			this.root.addEventListener('em-quiz:open', () => {
				this.track('em_quiz_open');
				this.start();
			});
			this.dialog.addEventListener('close', () => this.track('em_quiz_close'));
			if (this.dialog.open) {
				this.track('em_quiz_open');
				this.start();
			}
			return;
		}
		if (!('IntersectionObserver' in window)) {
			this.start();
			return;
		}
		const observer = new IntersectionObserver((entries) => {
			if (entries.some((entry) => entry.isIntersecting)) {
				observer.disconnect();
				this.start();
			}
		});
		observer.observe(this.root);
	}

	start() {
		if (this.started) {
			return;
		}
		this.started = true;
		if (this.config.intro) {
			this.els.log.append(el('li', { class: 'em-quiz__turn' }, botBubble(this.config.intro)));
		}
		this.revealNext();
		this.updateHeader();
		if (!this.viewed) {
			this.viewed = true;
			this.track('em_quiz_view');
		}
	}

	/**
	 * Add the next question to the log, after a short "typing" pause. The
	 * turn is rendered (invisibly) under the typing dots so it takes its
	 * final height up front and the chat doesn't shift when it appears;
	 * showing it just swaps the dots for the question (see _chat.scss).
	 */
	revealNext() {
		if (this.revealing || this.revealed >= this.questions.length) {
			return;
		}
		const index = this.revealed++;
		const turn = el('li', { class: 'em-quiz__turn', 'data-turn': index });
		this.turns[index] = turn;
		this.els.log.append(turn);
		this.renderTurn(index);

		const show = () => {
			this.revealing = false;
			turn.classList.remove('em-quiz__turn--typing');
			const typing = turn.querySelector('.em-quiz__typing-row');
			if (typing) {
				typing.remove();
			}
			this.renderDock();
			// Not while an earlier answer is being changed: stay with it.
			if (this.editing === null) {
				if (this.hasFocus()) {
					this.els.dockInner.querySelector('.em-quiz__choice').focus({ preventScroll: true });
				}
				this.scrollTo(turn);
			}
			this.updateHeader();
		};

		if (prefersReducedMotion()) {
			show();
			return;
		}
		this.revealing = true;
		turn.classList.add('em-quiz__turn--typing');
		turn.append(this.typingBubble());
		this.renderDock();
		this.scrollTo(turn);
		this.later(show, TYPING_MS);
	}

	/**
	 * (Re)build one question's turn: the question, then the chosen answer and
	 * "Change answer" once answered, or its choices and "Cancel" while the
	 * answer is being changed. While unanswered the turn is just the question;
	 * its choices are in the dock (renderDock()). The question bubble is kept
	 * so it doesn't replay its entrance, and the turn eases to its new height.
	 *
	 * @param {number}  index
	 * @param {Object}  [options]
	 * @param {boolean} [options.focus]       Focus the choices (when changing the answer).
	 * @param {boolean} [options.focusChange] Focus the "Change answer" link.
	 * @param {DOMRect} [options.morphFrom]   Where the picked choice was; the answer glides from there.
	 */
	renderTurn(index, { focus = false, focusChange = false, morphFrom = null } = {}) {
		const turn = this.turns[index];
		const question = this.questions[index];
		const answer = this.answers[index];
		const questionId = `${this.id}-q${index}`;
		const editing = this.editing === index;
		const before = hold(turn);

		if (!turn.firstElementChild) {
			turn.append(botBubble(question.text, questionId));
		}
		while (turn.firstElementChild.nextElementSibling) {
			turn.firstElementChild.nextElementSibling.remove();
		}

		if (editing) {
			const choices = this.choices(index);
			turn.append(choices, el('div', { class: 'em-quiz__row em-quiz__row--user' }, el('button', {
				type: 'button', class: 'em-quiz__change', 'data-em-quiz-cancel': true, 'aria-describedby': questionId, text: this.strings.cancel,
			})));
			if (focus) {
				(choices.querySelector('[aria-pressed="true"]') || choices.querySelector('.em-quiz__choice')).focus({ preventScroll: true });
			}
		} else if (answer !== null) {
			const bubble = el('p', { class: 'em-quiz__bubble em-quiz__bubble--user', text: question.answers[answer].label });
			turn.append(el('div', { class: `em-quiz__row em-quiz__row--user${morphFrom ? ' em-quiz__row--morph' : ''}` }, bubble));
			if (morphFrom) {
				this.fly(morphFrom, bubble);
			}
			if (this.phase !== 'submitted') {
				const change = el('button', {
					type: 'button',
					class: 'em-quiz__change',
					'data-change': index,
					'aria-describedby': questionId,
					text: this.strings.changeAnswer,
				});
				turn.append(el('div', { class: 'em-quiz__row em-quiz__row--user' }, change));
				if (focusChange) {
					change.focus({ preventScroll: true });
				}
			}
		}

		this.resize(turn, before);
		if (editing || focusChange) {
			this.scrollTo(turn);
		}
	}

	/**
	 * The question whose choices the dock shows: the next unanswered one (once
	 * it's in the log). None while an answer is being changed, so the only
	 * choices showing are the ones under that question.
	 *
	 * @return {number|null}
	 */
	dockIndex() {
		if (this.phase === 'submitted' || this.editing !== null) {
			return null;
		}
		const next = this.answers.indexOf(null);
		return next !== -1 && next < this.revealed ? next : null;
	}

	/**
	 * Show the open question's choices in the dock under the chat. While the
	 * question is "typing" its choices are laid out but invisible
	 * (_chat.scss), so the dock keeps its height from one question to the
	 * next. When the content does change, the dock eases to its new height.
	 *
	 * @param {Object}  [options]
	 * @param {boolean} [options.focus] Focus the choices.
	 */
	renderDock() {
		const { dock, dockInner } = this.els;
		const index = this.dockIndex();
		dock.classList.toggle('em-quiz__dock--typing', this.revealing);

		if (index !== this.dockKey) {
			this.dockKey = index;
			// The first question's choices appear with the quiz; later changes
			// (including opening from empty) ease.
			const ease = this.answers.some((a) => a !== null);
			const before = ease ? hold(dock, true) : 0;
			dockInner.replaceChildren(...(index === null ? [] : [this.choices(index)]));
			dockInner.hidden = index === null;
			this.resize(dock, before, ease);
		}
	}

	/**
	 * A question's choices, for the dock or (when changing the answer) its turn.
	 *
	 * @param {number} index
	 * @return {HTMLElement}
	 */
	choices(index) {
		const question = this.questions[index];
		const answer = this.answers[index];
		const choices = el('div', {
			class: 'em-quiz__choices',
			role: 'group',
			'aria-labelledby': `${this.id}-q${index}`,
			'data-question': index,
		}, el('p', { class: 'em-quiz__choose', text: this.strings.chooseOne }));

		question.answers.forEach((option, a) => {
			choices.append(el('button', {
				type: 'button',
				class: 'em-quiz__choice',
				'data-choice': a,
				'aria-pressed': answer === a ? 'true' : 'false',
				text: option.label,
			}));
		});
		return choices;
	}

	/**
	 * Ease a turn (or the dock) from its previous height to its new one, so
	 * content around it glides instead of jumping.
	 *
	 * @param {HTMLElement} turn
	 * @param {number}      before     Height before the change.
	 * @param {boolean}     [fromZero] Ease from an empty node too.
	 */
	resize(turn, before, fromZero = false) {
		// Natural height from the content (layout boxes, ignoring entrance
		// transforms), measured without releasing the held height.
		const content = [...turn.children].filter((child) => !child.classList.contains('em-quiz__typing-row')).pop();
		const after = content ? content.offsetTop + content.offsetHeight : 0;
		if ((!before && !fromZero) || Math.abs(before - after) < 1 || prefersReducedMotion()) {
			this.resizes.delete(turn);
			turn.style.height = '';
			turn.style.overflow = '';
			return;
		}
		this.resizes.set(turn, { from: before, to: after, start: performance.now() });
		this.animate();
	}

	onClick(event) {
		this.interacted = true;
		const target = event.target instanceof Element ? event.target : null;
		if (!target) {
			return;
		}
		const choice = target.closest('[data-choice]');
		if (choice) {
			this.select(Number(choice.closest('[data-question]').dataset.question), Number(choice.dataset.choice));
			return;
		}
		if (target.closest('[data-em-quiz-cancel]')) {
			this.cancelChange();
			return;
		}
		const change = target.closest('[data-change]');
		if (change) {
			this.changeAnswer(Number(change.dataset.change));
			return;
		}
		if (target.closest('[data-em-quiz-restart]')) {
			this.restart();
		}
	}

	/**
	 * Answer (or re-answer) one question. Other answers are untouched.
	 *
	 * @param {number} index
	 * @param {number} answer
	 */
	select(index, answer) {
		if (this.phase === 'submitted' || this.choosing) {
			return;
		}
		const previous = this.answers[index];
		this.answers[index] = answer;
		if (previous === null) {
			this.track('em_quiz_answer', this.answerParams(index));
		} else if (previous !== answer) {
			this.track('em_quiz_answer_change', {
				...this.answerParams(index),
				quiz_previous_answer: this.questions[index].answers[previous].value,
			});
		}
		if (this.editing === index) {
			this.editing = null;
		}

		// Like a chat: the pick is confirmed in place and the other choices
		// fade, then it moves up as the answer while the chat moves on.
		const choices = this.root.querySelector(`.em-quiz__choices[data-question="${index}"]`);
		const chosen = choices && choices.querySelector(`[data-choice="${answer}"]`);
		if (!chosen || prefersReducedMotion()) {
			this.answered(index);
			return;
		}
		choices.querySelectorAll('[data-choice]').forEach((button) => {
			button.setAttribute('aria-pressed', button === chosen ? 'true' : 'false');
		});
		choices.classList.add('em-quiz__choices--chosen');
		this.choosing = true;
		this.later(() => {
			this.choosing = false;
			this.answered(index, chosen.getBoundingClientRect());
		}, CHOOSE_MS);
	}

	/**
	 * Show a question as answered, then move on: the next question, or the
	 * result once all are answered.
	 *
	 * @param {number} index
	 * @param {DOMRect} [morphFrom] Where the picked choice was.
	 */
	answered(index, morphFrom = null) {
		if (this.dockKey === null) {
			// After a change the dock reopens (with the open question, if any)
			// first, so scrolling to the answer allows for it.
			this.renderDock();
		}
		this.renderTurn(index, { focusChange: true, morphFrom });
		if (this.isComplete() && this.phase === 'questions' && this.flight) {
			// The last answer floats up like the others: the dock keeps its
			// height with the choices hidden, as when the next question is
			// "typing" (collapsing it now would pull the chat down). Once the
			// answer is in place the result takes the next question's place and
			// scrolls into view. Answers stay locked till then.
			this.choosing = true;
			this.els.dock.classList.add('em-quiz__dock--typing');
			this.flight.onLand = () => {
				this.choosing = false;
				if (this.isComplete()) {
					// Dock first, so scrolling to the result allows for it closing.
					this.renderDock();
					this.showResult();
					this.updateHeader();
				}
			};
			this.updateHeader();
			return;
		}
		if (this.isComplete()) {
			this.showResult();
		} else if (this.answers.slice(0, this.revealed).every((a) => a !== null)) {
			this.revealNext();
		}
		this.renderDock();
		this.updateHeader();
	}

	/**
	 * Reopen a question's choices. Only one answered question is open at a time.
	 *
	 * @param {number} index
	 */
	changeAnswer(index) {
		if (this.phase === 'submitted' || this.choosing) {
			return;
		}
		if (this.editing !== null && this.editing !== index) {
			const previous = this.editing;
			this.editing = null;
			this.renderTurn(previous);
		}
		this.editing = index;
		// Dock first, so scrolling to the turn allows for the dock closing.
		this.renderDock();
		this.renderTurn(index, { focus: true });
	}

	/**
	 * Keep the current answer of the question being changed.
	 */
	cancelChange() {
		if (this.editing === null || this.choosing) {
			return;
		}
		const index = this.editing;
		this.editing = null;
		// Dock first, so scrolling to the turn allows for the dock reopening.
		this.renderDock();
		this.renderTurn(index, { focusChange: true });
	}

	isComplete() {
		return this.answers.every((a) => a !== null);
	}

	result() {
		const answers = this.answers.map((a, q) => ({
			category: this.questions[q].answers[a].category,
			weight: this.questions[q].weight,
		}));
		return score(answers, this.config.scoring);
	}

	/**
	 * Property values for the lead form's hidden fields. Mirrored on the
	 * server by em_quiz_hubspot_values().
	 *
	 * @param {{scores: Object, outcome: string}} result
	 * @return {Object<string, string>}
	 */
	hubspotValues(result) {
		const values = {};
		this.questions.forEach((question, q) => {
			values[question.property] = question.answers[this.answers[q]].value;
		});
		const { properties } = this.config;
		values[properties.result] = this.config.outcomes[result.outcome].value;
		values[properties.scores] = formatScores(result.scores);
		values[properties.session] = this.sessionId;
		return values;
	}

	/**
	 * Show or update the result and the lead form's hidden fields.
	 */
	showResult() {
		const result = this.result();
		const first = this.phase === 'questions';
		const previousOutcome = this.outcome;
		const changed = result.outcome !== previousOutcome;
		this.phase = 'complete';
		this.outcome = result.outcome;

		const resultParams = {
			quiz_result: this.config.outcomes[result.outcome].value,
			quiz_scores: formatScores(result.scores),
		};
		if (first) {
			this.track('em_quiz_complete', resultParams);
		} else if (changed) {
			this.track('em_quiz_result_change', {
				...resultParams,
				quiz_previous_result: this.config.outcomes[previousOutcome].value,
			});
		}

		const toggle = (container) => {
			if (!container) {
				return;
			}
			container.querySelectorAll('[data-outcome]').forEach((node) => {
				node.hidden = node.dataset.outcome !== result.outcome;
			});
			container.hidden = false;
		};
		toggle(this.els.result);
		toggle(this.els.lead);

		if (this.lead) {
			this.lead.mount();
			this.lead.sync(this.hubspotValues(result));
		}

		const panel = this.els.result.querySelector(`[data-outcome="${result.outcome}"]`);
		if (first) {
			this.scrollTo(panel, 'start');
			if (this.hasFocus()) {
				panel.focus({ preventScroll: true });
			}
		} else if (changed) {
			const { heading } = this.config.outcomes[result.outcome];
			this.announce(format(this.strings.updated, heading || result.outcome));
		}
	}

	onLeadSubmitted() {
		this.phase = 'submitted';
		const open = this.editing;
		this.editing = null;
		this.track('em_quiz_lead_submit', { quiz_result: this.config.outcomes[this.outcome].value });
		// Drop the "Change answer" links without rebuilding (and re-animating)
		// the turns; only a question still open for changing is re-rendered.
		if (open !== null) {
			this.renderTurn(open);
		}
		this.renderDock();
		this.els.log.querySelectorAll('[data-change]').forEach((change) => change.parentElement.remove());
		this.els.lead.hidden = true;
		this.els.thanks.hidden = false;
		this.updateHeader();
		const panel = this.els.thanks.querySelector('.em-quiz__panel');
		this.scrollTo(panel);
		panel.focus({ preventScroll: true });
	}

	restart() {
		this.track('em_quiz_restart', { quiz_result: this.outcome ? this.config.outcomes[this.outcome].value : null });
		this.reset();
		this.els.log.replaceChildren();
		[this.els.result, this.els.lead, this.els.thanks].forEach((node) => {
			if (node) {
				node.hidden = true;
			}
		});
		if (this.lead) {
			this.lead.reset();
		}
		this.stopScroll();
		this.land();
		this.resizes.clear();
		this.els.dock.style.height = '';
		this.els.dock.style.overflow = '';
		this.els.dockInner.replaceChildren();
		this.els.dockInner.hidden = true;
		this.els.body.scrollTop = 0;
		this.start();
		this.later(() => {
			const first = this.els.dockInner.querySelector('.em-quiz__choice');
			if (first) {
				first.focus({ preventScroll: true });
			}
		}, prefersReducedMotion() ? 0 : TYPING_MS + 50);
	}

	/**
	 * Analytics event with the quiz's common parameters.
	 *
	 * @param {string} event
	 * @param {Object} [params]
	 */
	track(event, params = {}) {
		// Where the visitor is after the event: the next unanswered question,
		// "details" once the result shows, "done" after the lead form.
		const answered = this.answers.filter((a) => a !== null).length;
		const next = this.answers.indexOf(null);
		let step = next === -1 ? 'details' : `question_${next + 1}`;
		if (this.phase === 'submitted') {
			step = 'done';
		}
		pushEvent(this.root, event, {
			quiz_id: this.config.id,
			quiz_name: this.config.slug,
			quiz_session: this.sessionId,
			quiz_display: this.dialog ? 'modal' : 'inline',
			quiz_total: this.questions.length,
			quiz_answered: answered,
			quiz_step: this.started ? step : 'not_started',
			...params,
		});
	}

	/**
	 * @param {number} index Question index.
	 * @return {Object}
	 */
	answerParams(index) {
		const answer = this.questions[index].answers[this.answers[index]];
		return {
			quiz_question: index + 1,
			quiz_answer: answer.value,
			quiz_answer_category: answer.category,
		};
	}

	updateHeader() {
		const total = this.questions.length;
		const answered = this.answers.filter((a) => a !== null).length;
		let status;
		if (this.phase === 'submitted') {
			status = this.strings.done;
		} else if (this.phase === 'complete' || this.isComplete()) {
			status = this.strings.yourDetails;
		} else {
			const current = this.answers.indexOf(null) + 1;
			status = format(this.strings.questionOf, current, total);
		}
		this.els.status.textContent = status;
		this.els.progress.setAttribute('aria-valuenow', answered);
		this.els.progress.style.setProperty('--em-quiz-progress', `${(answered / total) * 100}%`);
	}

	typingBubble() {
		return el(
			'div',
			{ class: 'em-quiz__row em-quiz__typing-row' },
			el(
				'p',
				{ class: 'em-quiz__bubble em-quiz__bubble--bot em-quiz__typing' },
				el('span', { class: 'em-quiz__sr-only', text: this.strings.typing }),
				el('span', { class: 'em-quiz__dot', 'aria-hidden': 'true' }),
				el('span', { class: 'em-quiz__dot', 'aria-hidden': 'true' }),
				el('span', { class: 'em-quiz__dot', 'aria-hidden': 'true' }),
			),
		);
	}

	announce(message) {
		this.els.announce.textContent = '';
		this.later(() => {
			this.els.announce.textContent = message;
		}, 50);
	}

	/**
	 * Whether the visitor is interacting with this quiz, so moving focus or
	 * scrolling won't pull them away from something else on the page. Tracks
	 * clicks too: Safari doesn't focus buttons on click.
	 *
	 * @return {boolean}
	 */
	hasFocus() {
		return this.interacted || this.root.contains(document.activeElement);
	}

	/**
	 * Scroll the quiz body (never the page) to show a node. "nearest" brings
	 * its bottom into view without hiding its top; "start" aligns its top.
	 * Measured against the layout that running resizes will end at, so the
	 * scroll and the resizes finish together in one motion.
	 *
	 * @param {HTMLElement} node
	 * @param {string}      [block] "nearest" or "start".
	 */
	scrollTo(node, block = 'nearest') {
		if (!node || !this.started) {
			return;
		}
		const { body } = this.els;
		const from = body.scrollTop;
		const gap = 20;
		const view = body.getBoundingClientRect();
		const rect = node.getBoundingClientRect();

		// How much running resizes will still move the node and the end of the
		// chat. The dock is stuck over the bottom of the body, so the visible
		// chat ends at its final top edge.
		let above = 0;
		let inside = 0;
		let total = 0;
		let dock = this.els.dock.offsetHeight;
		this.resizes.forEach(({ to }, turn) => {
			const box = turn.getBoundingClientRect();
			const change = box.height - to;
			total += change;
			if (turn === this.els.dock) {
				dock = to;
			} else if (box.bottom <= rect.top + 1) {
				above += change;
			} else if (box.top < rect.bottom) {
				inside += change;
			}
		});
		const top = from + rect.top - view.top - gap - above;
		const bottom = from + rect.bottom - view.bottom + dock + gap - above - inside;
		const max = body.scrollHeight - body.clientHeight - total;

		let target = from;
		if (block === 'start' || top < from) {
			target = top;
		} else if (bottom > from) {
			target = Math.min(top, bottom);
		}
		target = Math.max(0, Math.min(target, max));
		if (Math.abs(target - from) < 1) {
			return;
		}
		if (prefersReducedMotion()) {
			body.scrollTop = target;
			return;
		}
		const duration = Math.min(LONG_SCROLL_MS, Math.max(MOTION_MS, Math.abs(target - from) * SCROLL_MS_PER_PX));
		this.scrolling = {
			from, to: target, start: performance.now(), duration,
		};
		this.animate();
	}

	/**
	 * Float the answer up from the picked choice to its place in the chat, so
	 * the choice appears to become the answer. A copy flies on top of the
	 * frame: the real bubble would be clipped by its turn (held at its old
	 * height) and painted under the dock on the way.
	 *
	 * @param {DOMRect}     from   The picked choice, measured before it was removed.
	 * @param {HTMLElement} bubble The new answer bubble.
	 */
	fly(from, bubble) {
		this.land();
		const ghost = bubble.cloneNode(true);
		ghost.removeAttribute('id');
		ghost.classList.add('em-quiz__bubble--flying');
		ghost.setAttribute('aria-hidden', 'true');
		ghost.style.width = `${bubble.getBoundingClientRect().width}px`;
		bubble.style.visibility = 'hidden';
		this.root.append(ghost);
		this.flight = {
			ghost, bubble, from, start: performance.now(),
		};
		this.place(0);
		this.animate();
	}

	/**
	 * Move the flying answer part of the way from the choice to the real
	 * bubble, wherever scrolling and resizing have put it this frame.
	 *
	 * @param {number} progress Eased, 0 to 1.
	 */
	place(progress) {
		const { ghost, bubble, from } = this.flight;
		const to = bubble.getBoundingClientRect();
		const frame = this.root.getBoundingClientRect();
		const left = frame.left + this.root.clientLeft;
		const top = frame.top + this.root.clientTop;
		const x = from.right + (to.right - from.right) * progress - to.width - left;
		const y = from.top + (to.top - from.top) * progress - top;
		ghost.style.transform = `translate(${x}px, ${y}px)`;
		// Never over the header, if the answer's place has scrolled under it.
		const hidden = Math.max(0, this.els.body.getBoundingClientRect().top - top - y);
		ghost.style.clipPath = hidden ? `inset(${hidden}px 0 0)` : '';
	}

	/**
	 * End a flight: the real bubble takes the copy's place.
	 */
	land() {
		if (!this.flight) {
			return;
		}
		const { ghost, bubble, onLand } = this.flight;
		ghost.remove();
		bubble.style.visibility = '';
		this.flight = null;
		if (onLand) {
			onLand();
		}
	}

	stopScroll() {
		this.scrolling = null;
	}

	animate() {
		if (!this.frame) {
			this.frame = requestAnimationFrame(this.tick);
		}
	}

	/**
	 * Advance every running resize and the scroll by the same eased progress.
	 *
	 * @param {number} now
	 */
	tick(now) {
		this.frame = null;
		const time = Math.max(now, performance.now());
		let running = this.tickResizes(time);
		if (this.flight) {
			const t = Math.min(1, (time - this.flight.start) / MOTION_MS);
			if (t < 1) {
				this.place(ease(t));
				running = true;
			} else {
				this.land();
			}
		}
		if (this.scrolling) {
			const {
				from, to, start, duration,
			} = this.scrolling;
			const t = Math.min(1, (time - start) / duration);
			this.els.body.scrollTop = from + (to - from) * ease(t);
			if (t < 1) {
				running = true;
			} else {
				this.scrolling = null;
			}
		}
		if (running) {
			this.animate();
		}
	}

	/**
	 * @param {number} time
	 * @return {boolean} Whether any resize is still running.
	 */
	tickResizes(time) {
		this.resizes.forEach(({ from, to, start }, turn) => {
			const t = Math.min(1, Math.max(0, (time - start) / MOTION_MS));
			if (t < 1) {
				turn.style.height = `${from + (to - from) * ease(t)}px`;
			} else {
				turn.style.height = '';
				turn.style.overflow = '';
				this.resizes.delete(turn);
			}
		});
		return this.resizes.size > 0;
	}

	later(fn, ms) {
		this.timers.push(setTimeout(fn, ms));
	}
}
